import { Box, Modal, Stack, styled, Typography } from "@mui/material";

import { background, black } from "@/theme/colors";
import { FCC } from "@/types/common";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { IconBox } from "@/components/elements/IconBox";
import { IconClose } from "@/assets/icons";
import { ConfirmLeaveModal } from "@/features/talent/components/talent-scan-image/ConfirmLeaveModal";
import { DuplicateTalentModal } from "@/features/talent/components/talent-common/DuplicateTalentModal";
import { DuplicatePassportModal } from "@/features/talent/components/talent-common/DuplicatePassportModal";
import { ScanUploadStep } from "@/features/talent/components/talent-scan-image/ScanUploadStep";
import { ScanResultStep } from "@/features/talent/components/talent-scan-image/ScanResultStep";
import { useScanImageState } from "@/features/talent/components/talent-scan-image/useScanImageState";
import { CurrentTalentProvider } from "@/features/talent/components/talent-scan-image/current-value/current-talent-context";
import { TTalentForm } from "@/features/talent/types/talentForm";

type TScanImageTalentModalProps = {
  modal: UseVisibleReturn;
  // Snapshot of the talent currently being edited. When provided (modal opened
  // from talent detail), every FormField inside the modal shows a 現在の値
  // helper line under the input. Pass null/undefined when opening from talent
  // list / new-talent flow.
  currentTalent?: TTalentForm | null;
};

export const ScanImageTalentModal: FCC<TScanImageTalentModalProps> = ({ modal, currentTalent = null }) => {
  const state = useScanImageState(modal, currentTalent);
  const {
    isResultData,
    onClose,
    confirmClose,
    confirmLeaveModal,
    duplicateTalentModal,
    duplicatePassportModal,
    duplicateTalents,
    duplicatePassports,
    pendingPassportNumber,
    handleSaveTalent,
  } = state;

  return (
    <CurrentTalentProvider currentTalent={currentTalent}>
      <Modal open={modal.visible} hideBackdrop={false} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <ModalContainer isResultData={isResultData}>
          <Stack
            bgcolor={background.disable}
            minHeight="56px"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
          >
            <Stack ml={3}>
              <Typography variant="body14Light" color={"#0B0C0D"}>
                人材情報｜面談情報や履歴書などファイルからAI取込
              </Typography>
            </Stack>
            <Stack mr={3} width={24}>
              <IconBox onClick={onClose} sx={{ p: 0 }}>
                <IconClose color={black[80]} fontSize={24} />
              </IconBox>
            </Stack>
          </Stack>
          {isResultData ? <ScanResultStep state={state} /> : <ScanUploadStep state={state} />}
        </ModalContainer>
      </Modal>
      <ConfirmLeaveModal modal={confirmLeaveModal} onOk={confirmClose} />
      <DuplicateTalentModal
        open={duplicateTalentModal.open}
        onClose={() => duplicateTalentModal.cancel()}
        onSave={handleSaveTalent}
        duplicateTalents={duplicateTalents}
      />
      <DuplicatePassportModal
        open={duplicatePassportModal.open}
        onClose={() => duplicatePassportModal.cancel()}
        duplicateTalents={duplicatePassports}
        passportNumber={pendingPassportNumber}
      />
    </CurrentTalentProvider>
  );
};

const ModalContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isResultData",
})<{ isResultData: boolean }>`
  width: ${({ isResultData }) => (isResultData ? "80vw" : "min(920px, 95vw)")};
  height: ${({ isResultData }) => (isResultData ? "min(789px, 95vh)" : "auto")};
  max-width: 80vw;
  min-width: min(1264px, 95vw);
  max-height: 95vh;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  outline: none;
  &:focus {
    outline: none;
  }
`;
